import React, { useState, useEffect, useContext } from 'react';
import axios from '../../axios-config';
import { OrganizationJobsContext } from '../../contexts/OrganizationJobsContext';
import { useOrganizations } from '../../contexts/OrganizationsContext';

type Assignment = {
    organizationJobAssignmentId?: number;
    organizationJobId: number;
    userId: string;
    name: string;
    studentName: string;
    jobStartDate: string;
    jobEndDate: string; // re-added end date
    jobPoints: number;
    createdAt: any;
    createdBy: string;
    studentAccount: any;
    organizationJob: any;
};

interface PopupProps {
    onClose: () => void;
    deepLinkAssignment?: any;
    // New props:
    studentInfo?: { id: string; fullName: string };
}

interface ModalProps {
    editItem?: Assignment;
    onClose: () => void;
    onSuccess: () => void;
    studentInfo?: { id: string; fullName: string };
    existingAssignments?: Assignment[]; // new prop for filtering available students
}

const OrganizationJobAssignmentModal = ({ editItem, onClose, onSuccess, studentInfo, existingAssignments }: ModalProps) => {
    // Retrieve available jobs from context
    const { organizationJobs } = useContext(OrganizationJobsContext);
    // useDefaultPoints flag for radio buttons
    const [useDefaultPoints, setUseDefaultPoints] = useState(true);
    // Initialize organizationJobId: from editItem if exists; otherwise use the first job in the list.
    const initialJobId = editItem?.organizationJobId || (organizationJobs.length > 0 ? organizationJobs[0].organizationJobId : 0);
    const [formState, setFormState] = useState<Assignment>({
        organizationJobId: initialJobId,
        organizationJobAssignmentId: editItem?.organizationJobAssignmentId,
        // use studentInfo id if provided, otherwise fallback to editItem's userId
        userId: studentInfo?.id || editItem?.userId || '',
        name: studentInfo?.fullName || editItem?.studentName || '', // added property
        studentName: studentInfo?.fullName || editItem?.studentName || '',
        jobStartDate: editItem?.jobStartDate ? editItem.jobStartDate.split('T')[0] : '',
        jobEndDate: editItem?.jobEndDate ? editItem.jobEndDate.split('T')[0] : '', // new
        jobPoints: editItem?.jobPoints || 0,
        studentAccount: editItem?.studentAccount || {}, // added property
        // Added missing fields:
        createdAt: editItem?.createdAt || new Date().toISOString(),
        createdBy: editItem?.createdBy || (studentInfo?.id || ''),
        organizationJob: organizationJobs.find((job: { organizationJobId: any; }) => job.organizationJobId === initialJobId) || {}
    });

    const [useEndDate, setUseEndDate] = useState(!!(editItem?.jobEndDate && editItem.jobEndDate !== ''));

    const selectedJob = organizationJobs.find((job: { organizationJobId: number; }) => job.organizationJobId === formState.organizationJobId);

    // Get all students from OrganizationsContext
    const { students } = useOrganizations();
    // Compute available students only in create mode (i.e. when editItem is not set)
    const availableStudents = !editItem
        ? students.filter(student => 
            // Changed: now comparing student.acornUser.id instead of student.id
            !existingAssignments?.some(assignment => assignment.userId === student.acornUser.id)
          )
        : [];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const updatedJobPoints = useDefaultPoints ? (selectedJob?.jobPoints ?? 0) : formState.jobPoints;
        const payload = {
            organizationJobAssignmentId: editItem?.organizationJobAssignmentId || 0,
            organizationJobId: formState.organizationJobId,
            userId: formState.userId,
            jobStartDate: new Date(formState.jobStartDate).toISOString(),
            jobEndDate: useEndDate ? new Date(formState.jobEndDate).toISOString() : null,
            jobPoints: updatedJobPoints,
            createdAt: editItem?.createdAt || new Date().toISOString(),
            createdBy: editItem?.createdBy || (studentInfo?.id || formState.userId)
        };
        try {
            if (editItem && editItem.organizationJobAssignmentId) {
                await axios.put(`/api/OrganizationJobAssignments/${editItem.organizationJobAssignmentId}`, payload);
            } else {
                await axios.post('/api/OrganizationJobAssignments', payload);
            }
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error(err);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow-lg w-11/12 sm:w-96">
                <h2 className="text-lg font-bold mb-4">{editItem ? 'Edit Assignment' : 'Create Assignment'}</h2>
                {/* Job ID Dropdown */}
                <div className="mb-2">
                    <label className="block mb-1">Job:</label>
                    <select
                        value={formState.organizationJobId}
                        onChange={e => setFormState({ ...formState, organizationJobId: Number(e.target.value) })}
                        className="border p-2 w-full"
                    >
                        {organizationJobs.map((job: { organizationJobId: React.Key | readonly string[] | null | undefined; jobName: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }) => (
                            <option key={String(job.organizationJobId)} value={String(job.organizationJobId)}>
                                {job.jobName}
                            </option>
                        ))}
                    </select>
                </div>
                {/* Student Field */}
                <div className="mb-2">
                    <label className="block mb-1">Student:</label>
                    { editItem ? (
                        <div className="p-2 border w-full rounded">
                            {studentInfo ? studentInfo.fullName : formState.studentName}
                        </div>
                    ) : (
                        // Changed: update onChange to use student.acornUser.id for matching
                        <select
                            value={formState.userId}
                            onChange={e => {
                                const selected = availableStudents.find(s => s.acornUser.id === e.target.value);
                                if (selected) {
                                    setFormState(prev => ({
                                        ...prev,
                                        userId: selected.acornUser.id,
                                        studentName: selected.acornUser.fullName
                                    }));
                                }
                            }}
                            className="border p-2 w-full"
                        >
                            <option value="">Select student</option>
                            {availableStudents.map((s, index) => (
                                <option key={`${s.acornUser.id}-${index}`} value={s.acornUser.id}>
                                    {s.acornUser.fullName} ({s.acornUser.school})
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                {/* Start Date */}
                <div className="mb-2">
                    <label className="block mb-1">Start Date:</label>
                    <input
                        type="date"
                        value={formState.jobStartDate}
                        onChange={e => setFormState({ ...formState, jobStartDate: e.target.value })}
                        className="border p-2 w-full"
                    />
                </div>
                {/* New Employment Status Radio Group */}
                <div className="mb-2">
                    <span className="block mb-1">Assignment Status:</span>
                    <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                checked={!useEndDate}
                                onChange={() => {
                                    setUseEndDate(false);
                                    setFormState({ ...formState, jobEndDate: '' });
                                }}
                                className="mr-1"
                            />
                            Currently Assigened
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                checked={useEndDate}
                                onChange={() => setUseEndDate(true)}
                                className="mr-1"
                            />
                            Specify End Date
                        </label>
                    </div>
                </div>
                {/* Conditionally show End Date input if employment has an end date */}
                {useEndDate && (
                    <div className="mb-2">
                        <label className="block mb-1">End Date:</label>
                        <input
                            type="date"
                            value={formState.jobEndDate}
                            onChange={e => setFormState({ ...formState, jobEndDate: e.target.value })}
                            className="border p-2 w-full"
                        />
                    </div>
                )}
                {/* Job Points Radio Group */}
                <div className="mb-2">
                    <span className="block mb-1">Job Points:</span>
                    <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                checked={useDefaultPoints}
                                onChange={() => setUseDefaultPoints(true)}
                                className="mr-1"
                            />
                            Use Class Default ({selectedJob?.jobPoints ?? ''})
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                checked={!useDefaultPoints}
                                onChange={() => setUseDefaultPoints(false)}
                                className="mr-1"
                            />
                            Override
                        </label>
                    </div>
                </div>
                {/* Only show override input if not using default */}
                {!useDefaultPoints && (
                    <div className="mb-2">
                        <label className="block mb-1">Override Points:</label>
                        <input
                            type="number"
                            value={formState.jobPoints}
                            onChange={e => setFormState({ ...formState, jobPoints: Number(e.target.value) })}
                            className="border p-2 w-full"
                        />
                    </div>
                )}
                <div className="flex gap-2 mt-4">
                    <button type="submit" className="bg-indigo-500 text-white px-4 py-2 rounded">
                        {editItem ? 'Update' : 'Create'}
                    </button>
                    <button type="button" onClick={onClose} className="bg-gray-300 px-4 py-2 rounded">
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

const OrganizationJobAssignmentsPopup: React.FC<PopupProps> = ({ onClose, deepLinkAssignment, studentInfo }) => {
    const { organizationJobs } = useContext(OrganizationJobsContext);
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [editItem, setEditItem] = useState<Assignment | null>(null);
    const [showForm, setShowForm] = useState(false);

    const fetchAssignments = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get('/api/OrganizationJobAssignments');
            setAssignments(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAssignments();
    }, []);

    // Deep link effect remains similar using updated payload structure
    useEffect(() => {
        if (deepLinkAssignment) {
            const linkAssignment =
                deepLinkAssignment.jobAssignment && deepLinkAssignment.jobAssignment.length > 0
                    ? deepLinkAssignment.jobAssignment[0]
                    : deepLinkAssignment;
            if (linkAssignment) {
                setEditItem({
                    organizationJobAssignmentId: linkAssignment.organizationJobAssignmentId,
                    organizationJobId:
                        linkAssignment.organizationJobId ||
                        (organizationJobs.length > 0 ? organizationJobs[0].organizationJobId : 0),
                    // Use full name if provided on deepLinkAssignment, otherwise fallback to linkAssignment.userId
                    userId: linkAssignment.studentAccount?.id || linkAssignment.userId,
                    name: linkAssignment.studentAccount?.fullName || deepLinkAssignment.name || '', // added property
                    studentName: linkAssignment.studentAccount?.fullName || deepLinkAssignment.name || '',
                    jobStartDate: linkAssignment.jobStartDate ? linkAssignment.jobStartDate.split('T')[0] : '',
                    jobEndDate: linkAssignment.jobEndDate ? linkAssignment.jobEndDate.split('T')[0] : '', // new
                    jobPoints: linkAssignment.jobPoints || 0,
                    studentAccount: linkAssignment.studentAccount || {}, // added property
                    createdAt: linkAssignment.createdAt || new Date().toISOString(),  // added missing property
                    createdBy: linkAssignment.createdBy || '',                         // added missing property
                    organizationJob: linkAssignment.organizationJob || {}                // added missing property
                });
                setShowForm(true);
            }
        }
    }, [deepLinkAssignment]);

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this assignment?')) return;
        try {
            await axios.delete(`/api/OrganizationJobAssignments/${id}`);
            fetchAssignments();
        } catch (err: any) {
            console.error(err);
        }
    };

    const handleSuccess = () => {
        fetchAssignments();
        setShowForm(false);
        setEditItem(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Job Assignments</h2>
                    <button onClick={onClose} className="text-gray-600">X</button>
                </div>
                {showForm ? (
                    <OrganizationJobAssignmentModal
                        editItem={editItem || undefined}
                        onClose={() => { setShowForm(false); setEditItem(null); }}
                        onSuccess={handleSuccess}
                        studentInfo={studentInfo}
                        existingAssignments={assignments} // pass assignments for filtering students
                    />
                ) : (
                    <>
                        <button
                            onClick={() => { setEditItem(null); setShowForm(true); }}
                            className="mb-4 bg-indigo-500 text-white px-4 py-2 rounded"
                        >
                            Create Assignment
                        </button>
                        {loading ? (
                            <div>Loading assignments...</div>
                        ) : error ? (
                            <div className="text-red-500">{error}</div>
                        ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {assignments.map((assignment) => (
                                    <div
                                        key={assignment.organizationJobAssignmentId}
                                        className="flex justify-between items-center border p-2 rounded"
                                    >
                                        <span>
                                        Student: {assignment.studentAccount?.fullName}<br/>Job: {assignment.organizationJob.jobName}, Points: {assignment.organizationJob.jobPoints}
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditItem({
                                                        organizationJobAssignmentId: assignment.organizationJobAssignmentId,
                                                        organizationJobId: assignment.organizationJobId,
                                                        userId: assignment.studentAccount?.id || assignment.userId,
                                                        name: assignment.studentAccount?.fullName || assignment.name || '',
                                                        studentName: assignment.studentAccount?.fullName || assignment.name || '',
                                                        jobStartDate: assignment.jobStartDate ? assignment.jobStartDate.split('T')[0] : '',
                                                        jobEndDate: assignment.jobEndDate ? assignment.jobEndDate.split('T')[0] : '',
                                                        jobPoints: assignment.jobPoints || 0,
                                                        studentAccount: assignment.studentAccount || {},
                                                        createdAt: assignment.createdAt || new Date().toISOString(),
                                                        createdBy: assignment.createdBy || '',
                                                        organizationJob: assignment.organizationJob || {}
                                                    });
                                                    setShowForm(true);
                                                }}
                                                className="px-2 py-1 bg-blue-500 text-white rounded"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => assignment.organizationJobAssignmentId && handleDelete(assignment.organizationJobAssignmentId)}
                                                className="px-2 py-1 bg-red-500 text-white rounded"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default OrganizationJobAssignmentsPopup;
